const FRENCH_MONTHS = {
  janvier: 1,
  fevrier: 2,
  février: 2,
  mars: 3,
  avril: 4,
  mai: 5,
  juin: 6,
  juillet: 7,
  aout: 8,
  août: 8,
  septembre: 9,
  octobre: 10,
  novembre: 11,
  decembre: 12,
  décembre: 12,
};

const PLACEHOLDER_PATTERNS = [
  /si non détect/i,
  /si aucune description/i,
  /\(non spécifié\)/i,
  /\[prospect_/i,
  /\[prospect_company\]/i,
  /\[prospect_job_title\]/i,
];

function normalizeString(value) {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim();
}

function isEmptyValue(value) {
  const normalized = normalizeString(value);
  if (!normalized) {
    return true;
  }
  const lower = normalized.toLowerCase();
  if (lower === "null" || lower === "undefined") {
    return true;
  }
  if (/^0+$/.test(normalized.replace(/\s/g, ""))) {
    return true;
  }
  return PLACEHOLDER_PATTERNS.some((pattern) => pattern.test(normalized));
}

function isValidEmail(value) {
  const email = normalizeString(value);
  if (!email || isEmptyValue(email)) {
    return false;
  }
  return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,10})+$/.test(email);
}

function normalizeWebsite(value) {
  const website = normalizeString(value);
  if (!website || isEmptyValue(website)) {
    return "";
  }
  if (/^https?:\/\//i.test(website)) {
    return website;
  }
  return "https://" + website;
}

/**
 * Decodes prefill JSON from ?data= query param.
 * Supports encodeURIComponent(JSON) and base64-encoded JSON.
 */
export function parsePrefillDataFromRoute(route) {
  const raw = route && route.query && route.query.data;
  if (!raw) {
    return null;
  }

  const attempts = [
    () => JSON.parse(decodeURIComponent(raw)),
    () => JSON.parse(raw),
    () => JSON.parse(base64ToUtf8(raw.replace(/-/g, "+").replace(/_/g, "/"))),
  ];

  for (let i = 0; i < attempts.length; i++) {
    try {
      const parsed = attempts[i]();
      if (parsed && typeof parsed === "object") {
        return parsed;
      }
    } catch (e) {
      // try next strategy
    }
  }

  return null;
}

/**
 * Encodes prefill payload for URL query string (for oliver-front).
 */
export function encodePrefillDataForUrl(payload) {
  return encodeURIComponent(JSON.stringify(payload));
}

export function parseMeetingDate(value, referenceDate) {
  const input = normalizeString(value);
  if (!input || isEmptyValue(input)) {
    return null;
  }

  const ref = referenceDate || new Date();

  const isoMatch = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10);
    const month = parseInt(isoMatch[2], 10);
    const day = parseInt(isoMatch[3], 10);
    if (isValidDateParts(year, month, day)) {
      return { year, month, day };
    }
    return null;
  }

  const frenchWithYear = input.match(/^(\d{1,2})\s+([a-zàâäéèêëïîôùûüç]+)\s+(\d{4})$/i);
  if (frenchWithYear) {
    const day = parseInt(frenchWithYear[1], 10);
    const month = frenchMonthToNumber(frenchWithYear[2]);
    const year = parseInt(frenchWithYear[3], 10);
    if (month && isValidDateParts(year, month, day)) {
      return { year, month, day };
    }
    return null;
  }

  const frenchDayMonth = input.match(/^(\d{1,2})\s+([a-zàâäéèêëïîôùûüç]+)$/i);
  if (frenchDayMonth) {
    const day = parseInt(frenchDayMonth[1], 10);
    const month = frenchMonthToNumber(frenchDayMonth[2]);
    const year = ref.getFullYear();
    if (month && isValidDateParts(year, month, day)) {
      return { year, month, day };
    }
    return null;
  }

  return null;
}

export function parseMeetingTime(value) {
  const input = normalizeString(value);
  if (!input || isEmptyValue(input)) {
    return null;
  }

  const colonMatch = input.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (colonMatch) {
    const hours = parseInt(colonMatch[1], 10);
    const minutes = parseInt(colonMatch[2], 10);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return padTime(hours, minutes);
    }
    return null;
  }

  const hMatch = input.match(/^(\d{1,2})h(\d{2})?$/i);
  if (hMatch) {
    const hours = parseInt(hMatch[1], 10);
    const minutes = hMatch[2] ? parseInt(hMatch[2], 10) : 0;
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return padTime(hours, minutes);
    }
    return null;
  }

  return null;
}

export function buildPrefillPlan(payload, referenceDate) {
  if (!payload || typeof payload !== "object") {
    return {
      formFields: {},
      parsedDate: null,
      parsedTime: null,
      canSkipToSchema: false,
      additionalGuests: [],
    };
  }

  const parsedDate = parseMeetingDate(payload.meeting_date, referenceDate);
  const parsedTime = parseMeetingTime(payload.meeting_time);

  return {
    formFields: extractFormFields(payload),
    parsedDate,
    parsedTime,
    canSkipToSchema: !!(parsedDate && parsedTime),
    additionalGuests: parseAdditionalGuests(payload.additional_guests),
  };
}

export function applyPrefillToModelForm(modelform, formFields) {
  if (!modelform || !formFields) {
    return;
  }

  Object.keys(formFields).forEach((key) => {
    if (formFields[key] !== undefined && formFields[key] !== null && formFields[key] !== "") {
      modelform[key] = formFields[key];
    }
  });
}

export function getCalendarNodays(month, year) {
  const firstDay = new Date(year, month - 1, 1);
  const dow = firstDay.getDay();
  return dow === 0 ? 6 : dow - 1;
}

export const CALENDAR_GRID_CELL_COUNT = 42;

export function isCalendarDaysGridComplete(calendarDays) {
  return (
    Array.isArray(calendarDays) &&
    calendarDays.length === CALENDAR_GRID_CELL_COUNT
  );
}

export function isDayAvailableInCalendar(day, month, year, calendarDays) {
  if (
    !day ||
    !isCalendarDaysGridComplete(calendarDays)
  ) {
    return false;
  }

  const nodays = getCalendarNodays(month, year);

  for (let i = 0; i < calendarDays.length; i++) {
    const dayNum = i + 1 - nodays;
    if (dayNum === day && calendarDays[i] === 2) {
      return true;
    }
  }

  return false;
}

export function findMatchingHourSlot(parsedTime, availableHours) {
  if (!parsedTime || !Array.isArray(availableHours) || availableHours.length === 0) {
    return null;
  }

  return (
    availableHours.find((hour) => normalizeHourString(hour) === parsedTime) || null
  );
}

function extractFormFields(payload) {
  const fields = {};

  if (!isEmptyValue(payload.prospect_firstname)) {
    fields.first_name = normalizeString(payload.prospect_firstname);
  }
  if (!isEmptyValue(payload.prospect_lastname)) {
    fields.last_name = normalizeString(payload.prospect_lastname);
  }
  if (!isEmptyValue(payload.prospect_company)) {
    fields.company_name = normalizeString(payload.prospect_company);
  }
  if (!isEmptyValue(payload.prospect_phone)) {
    fields.phone = normalizeString(payload.prospect_phone);
  }

  const website = normalizeWebsite(payload.prospect_website);
  if (website) {
    fields.url = website;
  }

  if (isValidEmail(payload.prospect_email)) {
    fields.email = normalizeString(payload.prospect_email);
  }

  if (!isEmptyValue(payload.description)) {
    fields.comments = normalizeString(payload.description);
  }

  const guests = parseAdditionalGuests(payload.additional_guests);
  if (guests.length > 0) {
    fields.emails = guests;
  }

  return fields;
}

function parseAdditionalGuests(guests) {
  if (!Array.isArray(guests) || guests.length === 0) {
    return [];
  }

  return guests
    .map((guest) => {
      if (typeof guest === "string") {
        if (isValidEmail(guest)) {
          return guestTag(guest, guest);
        }
        return null;
      }

      if (!guest || typeof guest !== "object") {
        return null;
      }

      const name = normalizeString(guest.name);
      const email = normalizeString(guest.email);

      if (isValidEmail(email)) {
        return guestTag(email, name || email);
      }

      if (name && !isEmptyValue(name)) {
        return guestTag(name, name);
      }

      return null;
    })
    .filter(Boolean);
}

function guestTag(value, name) {
  return {
    name: name,
    icon: "none",
    custom: true,
    value: value,
  };
}

function frenchMonthToNumber(monthName) {
  return FRENCH_MONTHS[normalizeString(monthName).toLowerCase()] || null;
}

function isValidDateParts(year, month, day) {
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function padTime(hours, minutes) {
  return (
    String(hours).padStart(2, "0") +
    ":" +
    String(minutes).padStart(2, "0")
  );
}

function normalizeHourString(hour) {
  const parsed = parseMeetingTime(hour);
  return parsed;
}

function base64ToUtf8(value) {
  return decodeURIComponent(
    Array.prototype.map
      .call(atob(value), function (char) {
        return "%" + ("00" + char.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );
}

function decodeRouteSegment(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  let normalized = String(value);
  normalized = normalized.replace(/=3D/gi, "%3D");
  normalized = normalized.replace(/%20/g, "");

  try {
    normalized = decodeURIComponent(normalized);
  } catch (e) {
    // keep raw value when decoding fails
  }

  normalized = normalized.replace(/\s+/g, "");
  return normalized || null;
}

export function normalizeCalendarHashForPayload(value) {
  if (
    value === null ||
    value === undefined ||
    value === false ||
    value === "" ||
    value === "false"
  ) {
    return false;
  }

  return value;
}

export function hasCalendarHash(value) {
  return normalizeCalendarHashForPayload(value) !== false;
}

// Link de oportunidad: /calendar/<cuenta>/<base64 "linkid:N">, sin campaign_hash.
export function isLinkIdHash(value) {
  if (!hasCalendarHash(value)) {
    return false;
  }
  try {
    const padded = value + "===".slice((value.length + 3) % 4);
    return /^linkid:\d+$/.test(atob(padded));
  } catch (e) {
    return false;
  }
}

export function canInsertCalendarActivity(businessContactHash, campaignHash) {
  return (
    hasCalendarHash(businessContactHash) &&
    (hasCalendarHash(campaignHash) || isLinkIdHash(businessContactHash))
  );
}

function getCalendarPathSegments(path) {
  if (!path) {
    return [];
  }

  const calendarIndex = path.indexOf("/calendar/");
  const myCalendarIndex = path.indexOf("/mycalendar/");

  if (calendarIndex === 0) {
    return path.slice("/calendar/".length).split("/").filter(Boolean);
  }

  if (myCalendarIndex === 0) {
    return path.slice("/mycalendar/".length).split("/").filter(Boolean);
  }

  return [];
}

/**
 * Resolves calendar link params from vue-router and pathname fallback.
 * Handles account_id values ending with "=" that can break optional path segments.
 */
export function parseCalendarRouteParams(route) {
  const params = (route && route.params) || {};
  const query = (route && route.query) || {};
  const pathSegments = getCalendarPathSegments((route && route.path) || "");

  let accountId = params.account_id || pathSegments[0] || null;
  let businessContactHash =
    params.business_contact_hash ||
    params.business_contact_hash2 ||
    pathSegments[1] ||
    query.business_contact_hash ||
    null;
  let campaignHash =
    params.campaign_hash ||
    params.campaign_hash2 ||
    pathSegments[2] ||
    query.campaign_hash ||
    null;

  return {
    account_id: decodeRouteSegment(accountId),
    business_contact_hash: decodeRouteSegment(businessContactHash),
    campaign_hash: decodeRouteSegment(campaignHash),
  };
}
